import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import css from './TenantAdminForm.scss';
import Icon from 'SF/MaterialIcon';
import { getWord } from 'utils/tenant';
import { isValidOrBlankEmail, isValidOrBlankPhoneNumber, isValidOrBlankUrl } from 'utils/text';
import StudentHelpPreferences from './StudentHelpPreferences';
import StaffHelpPreferences from './StaffHelpPreferences';
import HelpDeskInfo from 'helpPage/HelpDeskInfo';
import { omit } from 'lodash';

/**
 * @param {Object} props - the values for the support contact email, phone and url, as well as their setters.
 * @returns {JSX.Element} the Help Desk Information Configuration form
 */
function TenantAdminForm(props) {
    const [emailHasError, setEmailHasError] = useState(false);
    const [phoneHasError, setPhoneHasError] = useState(false);
    const [urlHasError, setUrlHasError] = useState(false);
    
    useEffect(() => {
        setEmailHasError(!isValidOrBlankEmail(props.email));
    }, [props.email]);
    
    useEffect(() => {
        setPhoneHasError(!isValidOrBlankPhoneNumber(props.phone));
    }, [props.phone]);
    
    useEffect(() => {
        setUrlHasError(!isValidOrBlankUrl(props.url));
    }, [props.url]);
    
    const starfish = getWord('Starfish', false);
    
    return (
        <div className={css['content']}>
            <h3 className='sf-admin-title'>Help Desk Information</h3>
            <p>This page allows you to display your {"institution's"} {starfish} Support contact information for users.</p>
            <p><b>Note for institutions using {starfish} Messaging Service text messaging:</b><br />
                <span className={css['semi-bold']}>At least one contact method is required for you to use the {starfish} Messaging Service text messaging. </span>
                If you do not provide at least one contact method for support, use of the service will be suspended
                until one is provided. Also, contact information provided will always display to all users in their
                profile regardless of the display settings below.
            </p>
            <h2 className={css['contact-info']}>Contact Information</h2>
            <div className={classnames({[css.error]: phoneHasError})}>
                <label htmlFor="phoneNumber">Phone Number</label>
                <input type="tel"
                       id="phoneNumber"
                       placeholder="e.g. 888-888-8888"
                       className={css['phone']}
                       value={props.phone}
                       onChange={e => props.setValue({phone: e.target.value})} />
                <span className={css['error-message']} aria-hidden={!phoneHasError}>
                    <Icon icon="error" aria-hidden="true" />Enter valid phone number e.g. 888-888-8888
                </span>
            </div>
            <div className={classnames({[css.error]: emailHasError})}>
                <label htmlFor="email">Email Address</label>
                <input type="email"
                       id="email"
                       placeholder="e.g. institutiontechsupport@email.com"
                       value={props.email}
                       onChange={e => props.setValue({email: e.target.value})} />
                <span className={css['error-message']} aria-hidden={!emailHasError}>
                    <Icon icon="error" aria-hidden="true" />Enter valid email e.g. supportemail@email.com
                </span>
            </div>
            <div className={classnames({[css.error]: urlHasError})}>
                <label htmlFor="website">Website</label>
                <textarea id="website"
                          rows="2"
                          placeholder={"e.g. https://your_institutions_" + starfish + "_support.com"}
                          value={props.url}
                          onChange={e => props.setValue({url: e.target.value})} />
                <span className={css['error-message']} aria-hidden={!urlHasError}>
                    <Icon icon="error" aria-hidden="true" />Enter valid URL e.g. http:// or https://www.website.com
                </span>
            </div>
            <div className={css['common-config']}>
                <label htmlFor="supportWebsiteDisplayText">Display Text for Website</label>
                <input type="text"
                       id="supportWebsiteDisplayText"
                       placeholder="Support Website"
                       value={props.supportWebsiteDisplayText}
                       onChange={e => props.setValue({supportWebsiteDisplayText: e.target.value})} />
            </div>
            <h2>Display Preferences</h2>
            <p>These preferences will control how contact information & other help content will display in {starfish}.</p>
            <div className={css['staff-preview']}>
                <label>Preview</label>
                <p>If enabled, staff will see the following content in {starfish}. Students may see this content <u>minus</u> the {starfish} Help Center link.</p>
                <HelpDeskInfo helpDeskInformation={omit(props, ['setValue'])} showStarfishHelpLink />
            </div>
            <div className={css['common-config']}>
                <label htmlFor="headerText">Header Text</label>
                <input type="text"
                       id="headerText"
                       placeholder="What can we help you with?"
                       value={props.headerText}
                       onChange={e => props.setValue({headerText: e.target.value})} />
            </div>
            <div className={css['common-config']}>
                <label htmlFor="sectionTitle">Title for Contact Information Section</label>
                <input type="text"
                       id="sectionTitle"
                       placeholder="Technical Support"
                       value={props.sectionTitle}
                       onChange={e => props.setValue({sectionTitle: e.target.value})} />
            </div>
            <StudentHelpPreferences setValue={props.setValue}
                                    showToStudents={props.showToStudents}
                                    studentDisplayText={props.studentDisplayText} />
            <StaffHelpPreferences productHelpName={props.productHelpName}
                                  setValue={props.setValue}
                                  showToStaff={props.showToStaff} />
        </div>
    );
}

TenantAdminForm.propTypes = {
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    showToStaff: PropTypes.bool.isRequired,
    showToStudents: PropTypes.bool.isRequired,
    headerText: PropTypes.string,
    productHelpName: PropTypes.string,
    sectionTitle: PropTypes.string,
    studentDisplayText: PropTypes.string,
    supportWebsiteDisplayText: PropTypes.string,
    setValue: PropTypes.func.isRequired
};

export default TenantAdminForm;
